import { useMemo, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ROUTES, TOAST_AUTO_CLOSE_TIME } from '@shared/constants';
import { BackButton } from '@components/BackButton';
import SectionLoading from '@components/SectionLoading';
import { Sidebar } from './components/sidebar';
import { Container, Main, LoadingOverlay } from './styles';
import { fetchStaffPage } from '../actions';
import { STAFF_PAGE_TYPES } from '../consts';
import { UserInformation } from './components/UserInformation';

/**
 * Wrapper component for all internal pages
 * Mounting: fetch user data by id
 */
export const StaffPageWrapper = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();

  const { loading, error } = useSelector((state) => state.get('staffPage').toJS());

  const currentPageType = useMemo(() => pathname.split('/').at(-1), [pathname]);

  /**
   * Back to staff list page
   */
  const goBackToStaff = () => {
    push(ROUTES.STAFF.LIST);
  };

  useEffect(() => {
    if (error) {
      toast.error(t('Informative.Negative.usersNotLoad'), {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    }
  }, [error]);

  useEffect(() => {
    dispatch(fetchStaffPage(id));
  }, []);

  /**
   * Render user page by type
   */
  const renderComponent = useCallback(() => {
    switch (currentPageType) {
      case STAFF_PAGE_TYPES.INFORMATION: return <UserInformation />;
      default: return null;
    }
  }, [currentPageType]);

  return (
    <Container>
      {loading && <LoadingOverlay><SectionLoading /></LoadingOverlay>}
      <Sidebar pageType={currentPageType} />
      <Main>
        <BackButton
          text={t('Actions.Navigation.backToStaff')}
          onClick={goBackToStaff}
          margin="0 0 15px 0"
        />
        {renderComponent()}
      </Main>
    </Container>
  );
};
